import {
  AppointmentRow,
  BillingAlertRow,
  ClinicalNoteRow,
  Kpi,
  TaskRow,
  TelemedLogRow,
  TimeSeries,
  StackedBarSeries,
  AiInsight,
  DateRange,
} from './types';
import { API_BASE } from '../config/env';

async function get(path: string, token?: string) {
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, { headers });
  if (!res.ok) {
    console.error(`Fetch failed for ${path}: ${res.status}`);
    // Fallback to empty/mock if needed, but for "real" data we should probably throw or return null
    // For now returning empty/default to prevent crash
    throw new Error(`Failed to fetch ${path}`);
  }
  return res.json();
}

async function post<T>(path: string, body: unknown, token?: string): Promise<T> {
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const payload = await res.json().catch(() => ({}));
    throw new Error(payload?.message || `Failed to post ${path}`);
  }
  return res.json();
}

function mapAppointmentStatus(status: string): AppointmentRow['status'] {
  switch (String(status || '').toUpperCase()) {
    case 'CHECKED_IN':
      return 'Checked-in';
    case 'COMPLETED':
      return 'Completed';
    case 'CANCELLED':
      return 'Cancelled';
    case 'NO_SHOW':
      return 'No-show';
    case 'PORTAL_BOOKED':
    case 'SCHEDULED':
    default:
      return 'Scheduled';
  }
}

function mapAppointmentsApiRow(appointment: any): AppointmentRow {
  const providerName = String(appointment?.provider?.name || '').trim() || 'Provider';
  const providerId = String(appointment?.provider?.id || '').trim();
  const start = String(appointment?.startTime || appointment?.start || '');
  const end = String(appointment?.endTime || appointment?.end || start);

  return {
    id: String(appointment?.id || ''),
    patient: String(appointment?.patient?.name || appointment?.patient || '').trim(),
    provider: { id: providerId, name: providerName },
    type: appointment?.isTelemed || appointment?.appointmentType?.isTelemed ? 'Telemed' : 'Clinic',
    start,
    end,
    status: mapAppointmentStatus(String(appointment?.status || '')),
    location: appointment?.location ? { id: String(appointment.location.id || ''), name: String(appointment.location.name || '') } : undefined,
  };
}

export async function fetchKpis(token?: string): Promise<Kpi[]> {
  return get('/dashboard/kpis', token);
}

export async function fetchAppointmentVolume(range: DateRange, token?: string): Promise<TimeSeries> {
  return get('/dashboard/appointment-volume', token);
}

export async function fetchNoShowVsCompleted(range: DateRange, token?: string): Promise<StackedBarSeries> {
  return get('/dashboard/no-show-vs-completed', token);
}

export async function fetchActivePatients(range: DateRange, token?: string): Promise<TimeSeries> {
  return get('/dashboard/active-patients', token);
}

export async function fetchTelemedUtilization(range: DateRange, token?: string): Promise<TimeSeries> {
  return get('/dashboard/telemed-utilization', token);
}

export async function fetchProviderWorkload(range: DateRange, token?: string): Promise<StackedBarSeries> {
  return get('/dashboard/provider-workload', token);
}

export async function fetchAiUsage(range: DateRange, token?: string): Promise<TimeSeries> {
  return get('/dashboard/ai-usage', token);
}

export async function fetchRevenueTrends(range: DateRange, token?: string): Promise<TimeSeries> {
  return get('/dashboard/revenue-trends', token);
}

export async function fetchTopDiagnoses(token?: string) {
  return get('/dashboard/top-diagnoses', token);
}

export async function fetchLabTurnaround(range: DateRange, token?: string): Promise<TimeSeries> {
  return get('/dashboard/lab-turnaround', token);
}

export async function fetchUpcomingAppointments(token?: string): Promise<AppointmentRow[]> {
  return get('/dashboard/upcoming-appointments', token);
}

export async function fetchAppointmentsRange(range: DateRange, token?: string): Promise<AppointmentRow[]> {
  const params = new URLSearchParams({
    start: range.start,
    end: range.end,
  });
  const rows = await get(`/appointments?${params.toString()}`, token);
  return Array.isArray(rows) ? rows.map((row) => mapAppointmentsApiRow(row)) : [];
}

export async function fetchRecentNotes(token?: string): Promise<ClinicalNoteRow[]> {
  // Map createdAt string to Date if needed, or ensuring types match
  return get('/dashboard/recent-notes', token);
}

export async function fetchBillingAlerts(token?: string): Promise<BillingAlertRow[]> {
  return get('/dashboard/billing-alerts', token);
}

export async function fetchTelemedLogs(token?: string): Promise<TelemedLogRow[]> {
  return get('/dashboard/telemed-logs', token);
}

export async function fetchTasks(token?: string): Promise<TaskRow[]> {
  return get('/dashboard/tasks', token);
}

export type TaskAssigneeOption = {
  id: string;
  name: string;
  title?: string | null;
};

export type CreateDashboardTaskInput = {
  title: string;
  description?: string;
  due?: string;
  assigneeId?: string;
  priority?: 'Low' | 'Med' | 'High';
};

export async function fetchTaskAssignees(token?: string): Promise<TaskAssigneeOption[]> {
  return get('/dashboard/task-assignees', token);
}

export async function createDashboardTask(input: CreateDashboardTaskInput, token?: string): Promise<TaskRow> {
  return post('/dashboard/tasks', input, token);
}

export async function fetchAiInsights(token?: string): Promise<AiInsight[]> {
  return get('/dashboard/ai-insights', token);
}
